import { Flex } from "@redotech/arbiter/flex";
import type { RedoListItem } from "@redotech/arbiter/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/arbiter/select-dropdown/redo-single-select-dropdown-input";
import { genericMemo } from "@redotech/react-util/component";
import { SettingsRowLabel } from "./settings-row-label";
import * as css from "./settings-rows.module.css";

/** Option for SettingsDropdownRow. */
export function settingsTextOption<T extends string>(
  value: T,
  text: string,
): RedoListItem<T> {
  return { id: value, type: "text", value, text };
}

/** Row with a single-select dropdown. */
export const SettingsDropdownRow = genericMemo(function SettingsDropdownRow<
  T extends string | null | undefined,
>({
  title,
  description,
  placeholder,
  options,
  value,
  setValue,
  disabled,
  disabledOptions,
  errorText,
}: {
  title: string;
  description?: string;
  placeholder?: string;
  options: RedoListItem<T>[];
  value: T | null;
  setValue(value: T): void;
  disabled?: boolean;
  /** Options shown but not selectable, with the tooltip explaining why. */
  disabledOptions?: { value: T; tooltip: string }[];
  /** Outlines the dropdown in red and shows this text beneath it. */
  errorText?: string;
}) {
  const selected = options.find((option) => option.value === value);
  // The narrow trigger shows only the name. Supporting text and badges stay in the menu.
  const selectedItem =
    selected?.type === "text"
      ? { ...selected, infoBadge: undefined, supportingText: undefined }
      : selected;
  return (
    <Flex
      align="center"
      gap="3xl"
      justify="space-between"
      px="2xl"
      py="xl"
      w="full"
    >
      <SettingsRowLabel description={description} title={title} />
      <Flex className={css.controlColumn} dir="column" gap="none" shrink={0}>
        <RedoSingleSelectDropdownInput<T>
          ariaLabel={title}
          description={errorText}
          disabled={disabled}
          disabledItems={
            disabledOptions &&
            options
              .filter((option) =>
                disabledOptions.some(
                  (disabled) => disabled.value === option.value,
                ),
              )
              .map((option) => option.id)
              .filter((id): id is string | number => typeof id !== "symbol")
          }
          disabledTooltips={
            disabledOptions &&
            Object.fromEntries(
              options.flatMap((option) => {
                const disabled = disabledOptions.find(
                  (disabled) => disabled.value === option.value,
                );
                return disabled ? [[option.id, disabled.tooltip]] : [];
              }),
            )
          }
          fitToAnchor={false}
          fullWidth
          options={options}
          optionSelected={({ item }) => setValue(item.value)}
          placeholder={placeholder}
          selectedItem={selectedItem}
          size="xs"
          state={errorText ? "error" : "default"}
        />
      </Flex>
    </Flex>
  );
});
